// ═══════════════════════════════════════════════════════════════
// TASK DRAWER — Slide-out task list for quick access
// Path: src/components/tasks/TaskDrawer.jsx
//
// Opens from the toolbar Tasks button. Provides:
//   - Check off tasks without leaving current view
//   - Inline quick-add (title + Enter)
//   - Link to full TasksView for filtering/editing
//
// Follows CultivationQueueDrawer pattern (380px right drawer).
// ═══════════════════════════════════════════════════════════════

import React, { useState, useMemo } from "react";
import {
  ClipboardCheck,
  X,
  Plus,
  CheckCircle2,
  Circle,
  Zap,
  User,
  Maximize2,
  Trash2,
  Calendar,
  AlertCircle,
  Tag,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { parseTaskDirective } from "../../utils/parseTaskDirective";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { TASK_PRIORITIES, TASK_CATEGORIES } from "../../constants/taskTypes";

// ── Helpers ──────────────────────────────────────────────────

const getPriority = (id) =>
  TASK_PRIORITIES.find((p) => p.id === id) || TASK_PRIORITIES[1];
const getCategory = (id) =>
  TASK_CATEGORIES.find((c) => c.id === id) || TASK_CATEGORIES[7];

const formatDueDate = (dueDate) => {
  if (!dueDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate + "T00:00:00");
  const diffDays = Math.round(
    (due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );
  if (diffDays < 0)
    return { label: `${Math.abs(diffDays)}d overdue`, color: "#ef4444" };
  if (diffDays === 0) return { label: "Today", color: "#f59e0b" };
  if (diffDays === 1) return { label: "Tomorrow", color: "#3b82f6" };
  if (diffDays <= 7) return { label: `${diffDays}d`, color: "#3b82f6" };
  return { label: `${Math.floor(diffDays / 7)}w`, color: "#6b7280" };
};

// ── Component ────────────────────────────────────────────────

export const TaskDrawer = ({ isOpen, onClose }) => {
  const {
    palette,
    members,
    directorTasks,
    openTasks,
    taskBadgeCount,
    completeTask,
    updateTask,
    deleteTask,
    addTask,
    navigate,
    userProfile,
    isAdmin,
  } = useApp();

  const [quickTitle, setQuickTitle] = useState("");
  const [showCompleted, setShowCompleted] = useState(false);

  const currentDirectorName = userProfile?.full_name || "";

  // Sort open tasks: overdue first, then by due date, undated last
  const sortedOpen = useMemo(() => {
    const todayStr = new Date().toISOString().split("T")[0];
    return [...openTasks].sort((a, b) => {
      // Overdue items first
      const aOverdue = a.dueDate && a.dueDate < todayStr;
      const bOverdue = b.dueDate && b.dueDate < todayStr;
      if (aOverdue && !bOverdue) return -1;
      if (!aOverdue && bOverdue) return 1;
      // Then by due date (nulls last)
      if (a.dueDate && b.dueDate) return a.dueDate.localeCompare(b.dueDate);
      if (a.dueDate && !b.dueDate) return -1;
      if (!a.dueDate && b.dueDate) return 1;
      // Then by priority
      return (getPriority(a.priority).sortOrder || 2) - (getPriority(b.priority).sortOrder || 2);
    });
  }, [openTasks]);

  const completedTasks = useMemo(
    () => directorTasks.filter((t) => t.status === "completed").slice(0, 20),
    [directorTasks]
  );

  // Live parse the quick-add input
  const parsed = useMemo(() => {
    if (!quickTitle.trim()) return null;
    return parseTaskDirective(quickTitle, members);
  }, [quickTitle, members]);

  const hasExtras =
    parsed &&
    (parsed.dueDate ||
      parsed.priority !== "normal" ||
      parsed.category !== "general" ||
      parsed.memberId);

  // Quick-add handler — uses parsed results
  const handleQuickAdd = async () => {
    if (!parsed || !parsed.title) return;
    await addTask({
      title: parsed.title,
      directorName: currentDirectorName,
      category: parsed.category,
      priority: parsed.priority,
      dueDate: parsed.dueDate,
      memberId: parsed.memberId,
    });
    setQuickTitle("");
  };

  const handleComplete = async (taskId) => {
    await completeTask(taskId);
  };

  const handleReopen = async (taskId) => {
    await updateTask(taskId, { status: "open", completedAt: null });
  };

  const handleDelete = async (taskId) => {
    await deleteTask(taskId);
  };

  const handleOpenFullView = () => {
    navigate("tasks");
    onClose();
  };

  if (!isOpen) return null;

  const overdueCount = taskBadgeCount;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 999,
          background: "rgba(0,0,0,0.15)",
        }}
      />

      {/* Drawer */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: 380,
          zIndex: 1000,
          background: palette.cardBg,
          borderLeft: `1px solid ${palette.cardBorder}`,
          boxShadow: "-4px 0 24px rgba(0,0,0,0.12)",
          display: "flex",
          flexDirection: "column",
          fontFamily: FONT_FAMILY,
          animation: "taskDrawerSlide 0.2s ease-out",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 18px",
            borderBottom: `1px solid ${palette.cardBorder}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: `linear-gradient(135deg, ${palette.accent1}15, ${palette.accent1}05)`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: `${palette.accent1}20`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ClipboardCheck size={ICON_SIZES.md} style={{ color: palette.accent1 }} />
            </div>
            <div>
              <div
                style={{
                  fontSize: FONT_SIZES.sm,
                  fontWeight: 700,
                  color: palette.text1,
                }}
              >
                My Tasks
              </div>
              <div style={{ fontSize: "0.6rem", color: palette.text3 }}>
                {openTasks.length} open
                {overdueCount > 0 ? ` · ${overdueCount} due` : ""}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <button
              onClick={handleOpenFullView}
              title="Open full Tasks view"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 4,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Maximize2 size={ICON_SIZES.sm} style={{ color: palette.text3 }} />
            </button>
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 4,
                display: "flex",
                alignItems: "center",
              }}
            >
              <X size={ICON_SIZES.md} style={{ color: palette.text3 }} />
            </button>
          </div>
        </div>

        {/* Smart quick-add bar */}
        <div
          style={{
            borderBottom: `1px solid ${palette.cardBorder}`,
          }}
        >
          <div
            style={{
              padding: "10px 18px",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Plus size={ICON_SIZES.sm} style={{ color: palette.text3, flexShrink: 0 }} />
            <input
              value={quickTitle}
              onChange={(e) => setQuickTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleQuickAdd();
              }}
              placeholder='Try "follow up with Sarah next Tuesday"'
              style={{
                flex: 1,
                padding: "6px 10px",
                borderRadius: 6,
                fontSize: FONT_SIZES.xs,
                fontFamily: FONT_FAMILY,
                background: palette.inputBg,
                color: palette.inputColor,
                border: `1px solid ${palette.inputBorder}`,
                outline: "none",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = palette.focusBorder;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = palette.inputBorder;
              }}
            />
          </div>

          {/* Parse preview strip */}
          {hasExtras && (
            <div
              style={{
                padding: "4px 18px 8px",
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              {parsed.dueDate && (
                <DrawerChip
                  icon={Calendar}
                  label={formatPreviewDate(parsed.dueDate)}
                  color="#3b82f6"
                />
              )}
              {parsed.priority !== "normal" && (
                <DrawerChip
                  icon={AlertCircle}
                  label={getPriority(parsed.priority).name}
                  color={getPriority(parsed.priority).color}
                />
              )}
              {parsed.category !== "general" && (
                <DrawerChip
                  icon={Tag}
                  label={getCategory(parsed.category).name}
                  color={getCategory(parsed.category).color}
                />
              )}
              {parsed.memberName && (
                <DrawerChip
                  icon={User}
                  label={parsed.memberName}
                  color={palette.accent1}
                />
              )}
            </div>
          )}
        </div>

        {/* Task list */}
        <div style={{ flex: 1, overflowY: "auto", padding: "4px 0" }}>
          {sortedOpen.length === 0 && (
            <div
              style={{
                padding: "40px 24px",
                textAlign: "center",
                color: palette.text3,
                fontSize: FONT_SIZES.xs,
                lineHeight: 1.6,
              }}
            >
              All clear — no open tasks.
              <br />
              Tasks are auto-created when you advance portfolio moves, or type
              above to add one.
            </div>
          )}

          {sortedOpen.map((task) => (
            <DrawerTaskRow
              key={task.id}
              task={task}
              palette={palette}
              members={members}
              onComplete={handleComplete}
              onDelete={handleDelete}
              onNavigate={(member) => {
                navigate("memberDetail", member);
                onClose();
              }}
            />
          ))}

          {/* Completed toggle */}
          {completedTasks.length > 0 && (
            <>
              <button
                onClick={() => setShowCompleted(!showCompleted)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 18px",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: FONT_FAMILY,
                  fontSize: "0.6rem",
                  color: palette.text3,
                  fontWeight: 600,
                  marginTop: 4,
                }}
              >
                {showCompleted ? "Hide" : "Show"} completed (
                {completedTasks.length})
              </button>
              {showCompleted &&
                completedTasks.map((task) => (
                  <DrawerTaskRow
                    key={task.id}
                    task={task}
                    palette={palette}
                    members={members}
                    onReopen={handleReopen}
                    onDelete={handleDelete}
                    onNavigate={(member) => {
                      navigate("memberDetail", member);
                      onClose();
                    }}
                    isCompleted
                  />
                ))}
            </>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "10px 18px",
            borderTop: `1px solid ${palette.cardBorder}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span
            style={{ fontSize: "0.6rem", color: palette.text3 }}
          >
            {openTasks.length} open · {completedTasks.length} completed
          </span>
          <button
            onClick={handleOpenFullView}
            style={{
              background: "none",
              border: `1px solid ${palette.cardBorder}`,
              borderRadius: 6,
              padding: "3px 10px",
              fontSize: "0.6rem",
              fontWeight: 600,
              color: palette.accent1,
              fontFamily: FONT_FAMILY,
              cursor: "pointer",
            }}
          >
            Full View →
          </button>
        </div>
      </div>

      {/* Animation */}
      <style>{`
        @keyframes taskDrawerSlide {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  );
};

// ── Compact task row for drawer ──────────────────────────────

const DrawerTaskRow = ({
  task,
  palette,
  members,
  onComplete,
  onReopen,
  onDelete,
  onNavigate,
  isCompleted = false,
}) => {
  const [hover, setHover] = useState(false);
  const completed = isCompleted || task.status === "completed";
  const priority = getPriority(task.priority);
  const category = getCategory(task.category);
  const dueInfo = formatDueDate(task.dueDate);

  const linkedMember = task.memberId
    ? members.find((m) => m.id === task.memberId)
    : null;
  const donorName = linkedMember
    ? `${linkedMember.firstName || ""} ${linkedMember.lastName || ""}`.trim()
    : null;

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        padding: "8px 18px",
        transition: "background 0.1s",
        opacity: completed ? 0.5 : 1,
        background: hover ? `${palette.accent1}08` : "transparent",
      }}
    >
      {/* Priority dot */}
      <div
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: priority.color,
          flexShrink: 0,
          marginTop: 7,
        }}
      />

      {/* Checkbox */}
      <button
        onClick={() => {
          if (completed && onReopen) onReopen(task.id);
          else if (!completed && onComplete) onComplete(task.id);
        }}
        title={completed ? "Reopen" : "Complete"}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: 0,
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
          marginTop: 1,
        }}
      >
        {completed ? (
          <CheckCircle2 size={ICON_SIZES.sm} style={{ color: "#10b981" }} />
        ) : (
          <Circle size={ICON_SIZES.sm} style={{ color: palette.text3 }} />
        )}
      </button>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontSize: FONT_SIZES.xs,
              fontWeight: completed ? 400 : 600,
              color: completed ? palette.text3 : palette.text1,
              textDecoration: completed ? "line-through" : "none",
              lineHeight: 1.3,
            }}
          >
            {task.title}
          </span>
          {task.isAuto && (
            <Zap size={ICON_SIZES.xs} style={{ color: "#f59e0b", flexShrink: 0 }} />
          )}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginTop: 2,
            flexWrap: "wrap",
          }}
        >
          {donorName && (
            <button
              onClick={() => linkedMember && onNavigate?.(linkedMember)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 2,
                background: `${palette.accent1}12`,
                border: "none",
                borderRadius: 4,
                padding: "0px 5px",
                fontSize: "0.55rem",
                fontFamily: FONT_FAMILY,
                color: palette.accent1,
                cursor: "pointer",
              }}
            >
              <User size={ICON_SIZES.xs} />
              {donorName}
            </button>
          )}
          <span
            style={{
              fontSize: "0.55rem",
              fontWeight: 600,
              color: category.color,
            }}
          >
            {category.name}
          </span>
          {dueInfo && (
            <span
              style={{
                fontSize: "0.55rem",
                fontWeight: 600,
                color: dueInfo.color,
              }}
            >
              {dueInfo.label}
            </span>
          )}
        </div>
      </div>

      {/* Delete (on hover) */}
      <button
        onClick={() => onDelete?.(task.id)}
        title="Delete task"
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: 2,
          display: "flex",
          alignItems: "center",
          opacity: hover ? 0.6 : 0,
          transition: "opacity 0.15s",
          flexShrink: 0,
          marginTop: 2,
        }}
      >
        <Trash2 size={ICON_SIZES.xs} style={{ color: palette.text3 }} />
      </button>
    </div>
  );
};

// ── Parse preview helpers ───────────────────────────────────────

const DrawerChip = ({ icon: Icon, label, color }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 3,
      padding: "1px 6px",
      borderRadius: 4,
      fontSize: "0.55rem",
      fontWeight: 600,
      fontFamily: FONT_FAMILY,
      color: color,
      background: `${color}15`,
    }}
  >
    <Icon size={ICON_SIZES.xs} />
    {label}
  </span>
);

const formatPreviewDate = (dateStr) => {
  const date = new Date(dateStr + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round(
    (date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  if (diffDays > 0 && diffDays <= 7) return dayNames[date.getDay()];
  return `${monthNames[date.getMonth()]} ${date.getDate()}`;
};
